import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import type { Plan } from '../v4/contracts'

type Step = Plan['steps'][number]

const INPUT =
  'w-full rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-cyan-400'

/** The historian aggregates a read_series step can take, in the words the plan column uses. */
const AGGREGATES: Array<[string, string]> = [
  ['raw', 'raw values'],
  ['min', 'minimum'],
  ['max', 'maximum'],
  ['mean', 'mean'],
  ['count_above', 'samples above'],
  ['excursions', 'time above'],
  ['resample', 'values for each interval'],
]

/**
 * Editing one step of the plan awaiting approval (the old node-properties panel's twin): the
 * title, and what the step's operation takes: the dates and aggregate of a historian read, the
 * query of a document search or tag lookup, the question of a records query. Save sends the whole
 * plan with this step changed to the engine's edit route, which checks it on the data before it
 * takes it as a new version, or refuses it with the reason. Keyed by the parent on the step and
 * plan version, so a new version starts a fresh draft.
 */
export default function StepEditor({
  plan,
  step,
  busy,
  error,
  onSave,
}: {
  plan: Plan
  step: Step
  busy: boolean
  /** The engine's refusal of the last edit, if any (the hook's error line). */
  error?: string
  /** True once the engine took the plan. */
  onSave: (plan: Plan) => Promise<boolean>
}) {
  const { t } = useTranslation()
  const op = step.op as Record<string, unknown> | undefined
  const kind = typeof op?.op === 'string' ? op.op : ''
  const period = op?.period as { from: string; to: string; label?: string } | undefined
  const [title, setTitle] = useState(step.title)
  const [from, setFrom] = useState(period?.from.slice(0, 10) ?? '')
  const [to, setTo] = useState(period?.to.slice(0, 10) ?? '')
  const [aggregate, setAggregate] = useState(String(op?.aggregate ?? 'max'))
  const [threshold, setThreshold] = useState(op?.threshold === undefined ? '' : String(op.threshold))
  const [query, setQuery] = useState(String(op?.query ?? op?.question ?? ''))
  const [saving, setSaving] = useState(false)
  const withThreshold = aggregate === 'count_above' || aggregate === 'excursions'

  const edited = (): Step => {
    const next: Step = { ...step, title: title.trim() || step.title }
    if (!op) return next
    const o: Record<string, unknown> = { ...op }
    if (kind === 'read_series') {
      const datesChanged = period && (from !== period.from.slice(0, 10) || to !== period.to.slice(0, 10))
      // New dates drop the words the period was read from ("last month"): they no longer hold.
      if (datesChanged && from && to) o.period = { from: `${from}T00:00:00Z`, to: `${to}T00:00:00Z` }
      o.aggregate = aggregate
      const n = Number(threshold)
      if (withThreshold && threshold.trim() !== '' && Number.isFinite(n)) o.threshold = n
      else delete o.threshold
    } else if (kind === 'find_docs' || kind === 'resolve_assets') {
      o.query = query.trim() || String(op.query ?? '')
    } else if (kind === 'db_ask') {
      o.question = query.trim() || String(op.question ?? '')
    }
    return { ...next, op: o as unknown as Step['op'] }
  }

  const save = async () => {
    setSaving(true)
    try {
      await onSave({ ...plan, steps: plan.steps.map((s) => (s.id === step.id ? edited() : s)) })
    } finally {
      setSaving(false)
    }
  }

  const disabled = busy || saving
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-input bg-background p-3 dark:bg-dark-card"
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
    >
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        {t('workflow:node.title_label', { defaultValue: 'Title' })}
        <input className={INPUT} value={title} disabled={disabled} onChange={(e) => setTitle(e.target.value)} />
      </label>
      {kind === 'read_series' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('workflow:report.rerun_from', { defaultValue: 'From' })}
              <input type="date" className={INPUT} value={from} disabled={disabled} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('workflow:report.rerun_to', { defaultValue: 'To' })}
              <input type="date" className={INPUT} value={to} disabled={disabled} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('workflow:node.aggregate', { defaultValue: 'Aggregate' })}
              <select className={INPUT} value={aggregate} disabled={disabled} onChange={(e) => setAggregate(e.target.value)}>
                {AGGREGATES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {withThreshold && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('workflow:node.threshold', { defaultValue: 'Threshold' })}
                <input type="number" step="any" className={INPUT} value={threshold} disabled={disabled} onChange={(e) => setThreshold(e.target.value)} />
              </label>
            )}
          </div>
        </>
      )}
      {(kind === 'find_docs' || kind === 'resolve_assets' || kind === 'db_ask') && (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {kind === 'db_ask'
            ? t('workflow:node.question', { defaultValue: 'Question' })
            : t('workflow:node.query', { defaultValue: 'Query' })}
          <input className={INPUT} value={query} disabled={disabled} onChange={(e) => setQuery(e.target.value)} />
        </label>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <IntuiButton type="submit" size="sm" disabled={disabled}>
          {saving
            ? t('workflow:node.saving_plan', { defaultValue: 'Checking the plan…' })
            : t('workflow:node.save_plan', { defaultValue: 'Save plan' })}
        </IntuiButton>
        <span className="text-xs text-muted-foreground">
          {t('workflow:node.edit_hint', { defaultValue: 'The engine checks an edited plan on the data before it takes it as a new version.' })}
        </span>
      </div>
    </form>
  )
}
